import { useMemo, useState } from 'react';
import { Check, Download, FileBarChart, TriangleAlert, UserX } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { useGetDismissalRegisterQuery } from '@/store/api';
import { institutionsApi } from '@/lib/api';
import { PageLayout } from '@/components/ui/PageLayout';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Mark } from '@/components/ui/Badge';
import { StatTile } from '@/components/ui/StatTile';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';
import { todayWAT } from '@/lib/datetime';

const INCLUDED = [
  'Every pickup event — released, failed, flagged — with its exact timestamp',
  'The child, their class and the gate officer by NAME, not just by id',
  'Offline events labelled separately, for conflict review',
  'Student IDs as well, for cross-reference with your own registry',
];

const OUTCOME: Record<string, { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  released: { label: 'Released', tone: 'success' },
  not_released: { label: 'Not released', tone: 'warning' },
  absent: { label: 'Absent', tone: 'neutral' },
};

const timeFmt = new Intl.DateTimeFormat('en-GH', { hour: '2-digit', minute: '2-digit' });

export function ReportsPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const [date, setDate] = useState(todayWAT());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: register, isFetching } = useGetDismissalRegisterQuery(
    { institutionId, date },
    { skip: !institutionId },
  );

  /*
    The children who did NOT go home come first.

    An end-of-day report sorted alphabetically buries the only rows that need
    somebody to do something underneath everybody who left normally.
  */
  const ordered = useMemo(() => {
    const rank = { not_released: 0, released: 1, absent: 2 } as const;
    return [...(register?.rows ?? [])].sort(
      (a, b) => rank[a.outcome] - rank[b.outcome] || a.fullName.localeCompare(b.fullName),
    );
  }, [register]);

  const downloadRegister = async () => {
    const res = await fetch(`/api/v1/institutions/${institutionId}/reports/dismissal.csv?date=${date}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('access_token') ?? ''}` },
    });
    if (!res.ok) {
      setError('The register could not be generated. Please try again.');
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `safepass-dismissal-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await institutionsApi.exportReport(institutionId, date, 'csv');
      if (!res.ok) {
        setError('The report could not be generated. Please try again.');
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `safepass-report-${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('The download did not complete. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title="Reports"
      subtitle="Export the dismissal record for a given day"
      breadcrumbs={[{ label: 'Insight' }, { label: 'Reports' }]}
      contained={false}
    >
      <div className="grid max-w-3xl gap-3 lg:grid-cols-5">
        <div className={cn(styles.panel, 'lg:col-span-3')}>
          <div className="flex items-start gap-3 border-b border-border px-4 py-3">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-info/25 bg-info/[0.09] text-[hsl(var(--record-ink-info))]">
              <FileBarChart size={15} />
            </span>
            <div>
              <h2 className={styles.sectionTitle}>Daily dismissal report</h2>
              <p className={cn(styles.meta, 'mt-0.5')}>
                One row per gate event, in CSV, ready for a spreadsheet.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 px-4 py-4">
            <Field
              label="Report date"
              htmlFor="report-date"
              hint="Days are bounded by West Africa Time, matching the gate's own day."
              className="max-w-[200px]"
            >
              <Input
                id="report-date"
                type="date"
                value={date}
                max={todayWAT()}
                onChange={(e) => setDate(e.target.value)}
              />
            </Field>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
              >
                <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <Button
                variant="primary"
                loading={loading}
                disabled={!institutionId}
                onClick={handleExport}
                icon={<Download size={13} />}
              >
                {loading ? 'Generating…' : 'Export CSV'}
              </Button>
            </div>
          </div>
        </div>

        <div className={cn(styles.panel, 'lg:col-span-2')}>
          <div className="border-b border-border px-4 py-3">
            <h3 className={styles.sectionTitle}>What's in the file</h3>
          </div>
          <ul className="flex flex-col gap-2 px-4 py-3.5">
            {INCLUDED.map((line) => (
              <li key={line} className="flex items-start gap-2 text-[11.5px] leading-relaxed text-muted-foreground">
                <Check
                  size={12}
                  className="mt-[3px] flex-shrink-0 text-[hsl(var(--record-ink-success))]"
                  aria-hidden
                />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/*
        ── The dismissal register ────────────────────────────────────────────

        The audit log answers "what happened", one event at a time. It cannot
        answer the question a school actually asks at the end of an afternoon,
        which is about the children it did NOT hear about: who was here today
        and has still not gone home?

        A child not being collected is not an event — it is the absence of one
        — so no amount of reading an event log surfaces it. This starts from
        the roll instead.
      */}
      <section className="mt-4 flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h2 className="text-[15px] font-bold tracking-tight text-foreground">Dismissal register</h2>
            <p className={cn(styles.meta, 'mt-0.5')}>
              Every child on the roll for {date}, and what happened to them.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download size={13} />}
            disabled={!register || register.onRoll === 0}
            onClick={() => void downloadRegister()}
          >
            Export register
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="On roll" value={register?.onRoll ?? 0} tone="neutral" />
          <StatTile label="Released" value={register?.released ?? 0} tone="success" />
          <StatTile
            label="Not released"
            value={register?.notReleased ?? 0}
            /* The only figure on this page anybody has to act on. */
            tone={(register?.notReleased ?? 0) > 0 ? 'danger' : 'success'}
            hint={(register?.notReleased ?? 0) > 0 ? 'Still not collected' : 'Everyone went home'}
          />
          <StatTile label="Absent" value={register?.absent ?? 0} tone="neutral" />
        </div>

        <div className="overflow-clip rounded-lg border border-border bg-card">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-border">
                <th className={cn(styles.label, 'px-3 py-2 text-left')}>Student</th>
                <th className={cn(styles.label, 'w-[130px] px-3 py-2 text-left max-sm:hidden')}>Class</th>
                <th className={cn(styles.label, 'w-[120px] px-3 py-2 text-left')}>Outcome</th>
                <th className={cn(styles.label, 'w-[90px] px-3 py-2 text-right')}>Released</th>
                <th className={cn(styles.label, 'w-[150px] px-3 py-2 text-left max-md:hidden')}>By</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && !register ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center">
                    <Spinner />
                  </td>
                </tr>
              ) : ordered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-[11.5px] text-muted-foreground">
                    <UserX size={18} className="mx-auto mb-1.5 opacity-40" />
                    No children on the roll for this day.
                  </td>
                </tr>
              ) : (
                ordered.map((r) => (
                  <tr key={r.studentId} className="border-b border-border last:border-b-0">
                    <td className="px-3 py-2">
                      <span className="font-semibold text-foreground">{r.fullName}</span>
                      {/* Only reachable by an offline device — the gate refuses
                          it online — so it is worth a flag rather than a row
                          that looks like any other. */}
                      {r.flagged && (
                        <Mark tone="danger" className="ml-2">
                          Released while marked absent
                        </Mark>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground max-sm:hidden">{r.studentClass}</td>
                    <td className="px-3 py-2">
                      <Mark tone={OUTCOME[r.outcome]!.tone}>{OUTCOME[r.outcome]!.label}</Mark>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {r.releasedAt ? timeFmt.format(new Date(r.releasedAt)) : '—'}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground max-md:hidden">
                      <span className="truncate">{r.releasedBy || '—'}</span>
                      {r.offline && (
                        <Mark tone="warning" className="ml-1.5">
                          Offline
                        </Mark>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

    </PageLayout>
  );
}
